import { ChevronDown, Layers } from 'lucide-react';
import { useId } from 'react';
import { MenuButton } from '../shell/Menu';
import { strings } from '../strings';
import { useOptionalDock, type DockApi } from './context';
import { windowState } from './layout';
import type { ToolWindowDefinition } from './registry';

const s = strings.dock;

function WindowButton({ dock, definition }: { dock: DockApi; definition: ToolWindowDefinition }) {
  const reasonId = useId();
  const { id, title } = definition;
  const Icon = definition.icon;
  const available = dock.available.has(id);
  const state = windowState(dock.state, id);
  // Shown: docked (also as a background tab), floating or maximized.
  const shown = available && state !== 'hidden';
  const badge = dock.badges.get(id) ?? {};
  const errors = badge.errors ?? 0;
  const warnings = badge.warnings ?? 0;
  const suffix =
    available && state === 'maximized'
      ? s.maximizedSuffix
      : available && state === 'floating' && !dock.narrow
        ? s.floatingSuffix
        : '';
  const details = [
    ...(available && errors > 0 ? [s.errors(errors)] : []),
    ...(available && warnings > 0 ? [s.warnings(warnings)] : []),
    ...(available && badge.live ? [s.runActive] : []),
  ];
  return (
    <>
      <button
        type="button"
        className="sb-window"
        aria-pressed={shown}
        aria-label={s.buttonLabel(title + suffix, details)}
        aria-disabled={available ? undefined : true}
        aria-describedby={available ? undefined : reasonId}
        aria-keyshortcuts={`Control+Shift+${definition.shortcut}`}
        title={available ? undefined : definition.unavailable}
        data-window-button={id}
        // A click keeps focus where it is, so a focused window can be hidden from here (spec 6.8.5).
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => dock.toggle(id)}
      >
        <Icon aria-hidden className="size-3" />
        <span>
          {title}
          {suffix}
        </span>
        {available && errors > 0 && (
          <span aria-hidden className="sb-count sb-count-danger">
            {errors}
          </span>
        )}
        {available && warnings > 0 && (
          <span aria-hidden className="sb-count sb-count-warning">
            {warnings}
          </span>
        )}
        {available && badge.live && <span aria-hidden className="sb-live" />}
      </button>
      {!available && (
        <span id={reasonId} className="sr-only">
          {definition.unavailable}
        </span>
      )}
    </>
  );
}

/** The status bar's tool-window buttons and Layout menu (spec 6.8.5). */
export function StatusBarWindows() {
  const dock = useOptionalDock();
  if (!dock) return null;
  return (
    <div className="ml-auto flex shrink-0 items-center gap-0.5">
      {dock.definitions.map((definition) => (
        <WindowButton key={definition.id} dock={dock} definition={definition} />
      ))}
      <MenuButton
        buttonClassName="sb-window"
        buttonContent={
          <>
            <Layers aria-hidden className="size-3" />
            {s.layout}
            <ChevronDown aria-hidden className="size-3" />
          </>
        }
        menuLabel={s.layoutMenu}
        placement="above-end"
        entries={[
          { kind: 'action', id: 'reset', label: s.resetLayout, onSelect: dock.resetLayout },
          { kind: 'action', id: 'dock-all', label: s.dockAll, onSelect: dock.dockAll },
          { kind: 'action', id: 'hide-all', label: s.hideAll, onSelect: dock.hideAll },
        ]}
      />
    </div>
  );
}
